import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import UrlAssembler from 'url-assembler';
import { ACDLError, InputStatus } from '../../../../models/error.model';
import { IPagingInfoWithOrder } from '../../../../models/paginationInfo.model';
import { IMainApiService } from '../../../../services/api.service';
import { IApiService } from '../../../../services/api.service.base';
import { IApiServiceExport } from '../../../../services/api.service.export';
import { IAuthStore } from '../../../auth/auth.store';
import { ExportDataFilter } from '../../../projects/projects.model';
import { ICanPublishProjectResponse } from '../../projectDetails.models';
import { IProjectDetailsSubmodule } from '../../services/IProjectDetailsSubmodule';
import { IProjectDetailsActivitiesStore } from './projectDetailsActivities.store';
import { IProjectDetailsActivitiesApiService } from './services/projectDetailsActivitiesApi.service';

export interface IProjectDetailsActivitiesBl extends IProjectDetailsSubmodule {
  getProjectActivitiesAsync(projectId: string, activities: string[], actor: string, pagingInfo: IPagingInfoWithOrder): Promise<void>;
  downloadExportAsync(projectId: string, backupId: string, dataFilter?: ExportDataFilter): Promise<void | ACDLError>;
  store: IProjectDetailsActivitiesStore;
}

export namespace IProjectDetailsActivitiesBl {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsActivitiesBl> = Symbol('PROJECT_DETAILS_ACTIVITIES_SERVICE');
}

@injectable()
export class ProjectDetailsActivitiesBl implements IProjectDetailsActivitiesBl {
  constructor(
    @inject(IProjectDetailsActivitiesStore.$) public readonly store: IProjectDetailsActivitiesStore,
    @inject(IProjectDetailsActivitiesApiService.$) private readonly projectDetailsActivitiesApiService: IProjectDetailsActivitiesApiService,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IAuthStore.$) public readonly authStore: IAuthStore,
    @inject(IApiServiceExport.$) public readonly exportApiServce: IApiServiceExport
  ) {
    makeObservable(this);
  }

  @action
  initialize(): void {
    this.store.activityLogStatus = InputStatus.empty();
  }

  @action
  cleanup(): void {
    this.store.activityLogStatus = InputStatus.empty();
  }

  @action
  validate(): void {
    this.store.activityLogStatus = InputStatus.valid();
  }

  @action
  handleCanPublishCheck(_: ICanPublishProjectResponse): void {
    this.store.activityLogStatus = InputStatus.valid();
  }

  getValidationErrors(): string[] {
    return this.store.activityLogStatus.errorCodes;
  }

  @action
  async downloadExportAsync(projectId: string, backupId: string, dataFilter: ExportDataFilter): Promise<void | ACDLError> {
    const url = UrlAssembler()
      .template('/Export/DownloadExportData')
      .query({
        projectId,
        backupId,
        dataFilter,
      })
      .toString();

    const result = await this.exportApiServce.getFileAsync(url);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  }

  @action
  async getProjectActivitiesAsync(projectId: string, activities: string[], actor: string, pagingInfo: IPagingInfoWithOrder): Promise<void> {
    this.store.showLoader = true;
    const result = await this.projectDetailsActivitiesApiService.getProjectActivitiesAsync({ projectId, activities, actor, ...pagingInfo });

    if (result instanceof ACDLError) {
      this.store.showLoader = false;
      return;
    }

    if (result.pagesCount > 0 && result.pagesCount < result.pageNumber) {
      await this.getProjectActivitiesAsync(projectId, activities, actor, { ...pagingInfo, pageNumber: result.pagesCount });
    } else {
      this.store.projectActivities.replace(result.data);
      this.store.projectActivitiesPaging = {
        ...this.store.projectActivitiesPaging,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
    }

    this.store.showLoader = false;
  }
}
